"use client";

import Link from "next/link";
import { useState } from "react";
import type { Locale } from "@/lib/i18n";

export type CalcPackage = {
  code: string;
  name: string;
  monthly: number;
  yearly: number | null;
  doctors: number;
  freeMonths: number | null;
};

export type CalcModule = {
  code: string;
  name: string;
  desc: string;
  monthly: number;
  perDoctor: boolean;
};

const MAX_DOCTORS = 99;

/**
 * Hekim sayısına göre kademe seçer, seçilen ek modüllerle aylık toplamı (KDV hariç)
 * hesaplar. Veri sunucudan hazır gelir; burada fiyat çekilmez ve yazılmaz.
 */
export function PricingCalculator({
  locale,
  packages,
  modules,
  demoHref,
}: {
  locale: Locale;
  packages: CalcPackage[];
  modules: CalcModule[];
  demoHref: string;
}) {
  const en = locale === "en";
  const fmt = (n: number) =>
    new Intl.NumberFormat(en ? "en-US" : "tr-TR", {
      style: "currency",
      currency: "TRY",
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0,
    }).format(n);

  const [doctors, setDoctors] = useState(1);
  const [picked, setPicked] = useState<string[]>([]);

  const pkg = packages.find((p) => doctors <= p.doctors) ?? null;
  const largest = packages[packages.length - 1];
  const chosen = modules.filter((m) => picked.includes(m.code));
  const modTotal = chosen.reduce((s, m) => s + m.monthly * (m.perDoctor ? doctors : 1), 0);

  const setDocs = (v: number) =>
    setDoctors(Math.min(MAX_DOCTORS, Math.max(1, Math.round(v) || 1)));
  const toggle = (code: string) =>
    setPicked((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const stepBtn =
    "flex h-10 w-10 items-center justify-center rounded-full border border-border text-lg text-foreground transition-colors hover:border-foreground/40 disabled:opacity-40";

  return (
    <div>
      <div className="rounded-2xl border border-border bg-card/40 p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <label htmlFor="calc-doctors" className="text-base font-medium text-foreground">
              {en ? "Number of dentists" : "Hekim sayısı"}
            </label>
            <p className="mt-1 text-sm text-muted">
              {en
                ? "The plan is picked automatically from your number of dentists."
                : "Paket, hekim sayınıza göre otomatik seçilir."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={stepBtn}
              onClick={() => setDocs(doctors - 1)}
              disabled={doctors <= 1}
              aria-label={en ? "Fewer dentists" : "Hekim sayısını azalt"}
            >
              −
            </button>
            <input
              id="calc-doctors"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_DOCTORS}
              value={doctors}
              onChange={(e) => setDocs(Number(e.target.value))}
              className="h-10 w-16 rounded-xl border border-border bg-background text-center text-base tabular-nums text-foreground"
            />
            <button
              type="button"
              className={stepBtn}
              onClick={() => setDocs(doctors + 1)}
              disabled={doctors >= MAX_DOCTORS}
              aria-label={en ? "More dentists" : "Hekim sayısını artır"}
            >
              +
            </button>
          </div>
        </div>
        <p className="mt-4 rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground/85">
          {pkg ? (
            <>
              {en ? "Recommended plan: " : "Önerilen paket: "}
              <strong className="font-semibold text-foreground">{pkg.name}</strong>
              {en
                ? ` · up to ${pkg.doctors} dentists · ${fmt(pkg.monthly)}/mo`
                : ` · ${pkg.doctors} hekime kadar · ${fmt(pkg.monthly)}/ay`}
            </>
          ) : en ? (
            `More than ${largest.doctors} dentists? We prepare a custom quote for your organization.`
          ) : (
            `${largest.doctors} hekimden büyük yapılar için özel teklif hazırlıyoruz.`
          )}
        </p>
      </div>

      <fieldset className="mt-6">
        <legend className="sr-only">{en ? "Add-on modules" : "Ek modüller"}</legend>
        <div className="grid gap-3 md:grid-cols-2">
          {modules.map((m) => {
            const on = picked.includes(m.code);
            return (
              <label
                key={m.code}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
                  on
                    ? "border-[#F97316]/60 bg-[#F97316]/[0.06]"
                    : "border-border hover:border-foreground/30"
                }`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(m.code)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#F97316]"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium text-foreground">{m.name}</span>
                    <span className="shrink-0 text-right text-sm tabular-nums text-foreground">
                      {fmt(m.monthly)}
                      <span className="text-xs text-muted">
                        {m.perDoctor ? (en ? " /dentist/mo" : " /hekim/ay") : en ? " /mo" : " /ay"}
                      </span>
                    </span>
                  </span>
                  {m.desc ? (
                    <span className="mt-1 block text-xs leading-relaxed text-muted">{m.desc}</span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="sticky bottom-4 z-10 mt-6 rounded-2xl border border-border bg-background/95 p-5 shadow-[0_18px_50px_-24px_rgba(0,0,0,0.35)] backdrop-blur md:p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-widest text-muted">
              {en ? "Monthly total · excl. VAT" : "Aylık toplam · KDV hariç"}
            </p>
            <p
              className="mt-1 text-3xl font-thin tracking-tight tabular-nums text-foreground"
              aria-live="polite"
            >
              {pkg ? fmt(pkg.monthly + modTotal) : en ? "Custom quote" : "Özel teklif"}
            </p>
            {pkg ? (
              <p className="mt-1 text-xs text-muted">
                {en ? `${pkg.name} plan ${fmt(pkg.monthly)}` : `${pkg.name} paketi ${fmt(pkg.monthly)}`}
                {chosen.length
                  ? en
                    ? ` + ${chosen.length} ${chosen.length === 1 ? "module" : "modules"} ${fmt(modTotal)}`
                    : ` + ${chosen.length} modül ${fmt(modTotal)}`
                  : ""}
              </p>
            ) : null}
            {pkg?.yearly && pkg.freeMonths ? (
              <p className="mt-1 text-xs text-muted">
                {en
                  ? `Plan billed annually: ${fmt(pkg.yearly)} (${pkg.freeMonths} months free)`
                  : `Paket yıllık ödemede ${fmt(pkg.yearly)} (${pkg.freeMonths} ay bedava)`}
              </p>
            ) : null}
          </div>
          <Link
            href={demoHref}
            className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#F97316] px-6 py-2.5 text-sm font-medium text-[#172554] transition-opacity hover:opacity-90"
          >
            {en ? "Get a demo & quote" : "Demo ve teklif al"} →
          </Link>
        </div>
      </div>
    </div>
  );
}
